import type { ArticleNode, UserProfileView } from '@/features/user-profile/types';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';

import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toastError } from '@/helpers/vfm/toast-helper';

import { useRoute, useRouter } from 'vue-router/composables';
import { computed, nextTick } from 'vue';
import {
	GetProfileByUrlDocument,
	GetProfileByUrlQuery,
	GetProfileByUrlQueryVariables,
} from '@/features/user-profile/graphql/queries/GetUserProfileByUrl.query';
import { useMounted } from '@vueuse/core';
import { ProfileSpecificLinkType } from '@/@types/graphql-types';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

export function useUserProfile() {
	const route = useRoute();
	const { country, language } = storeToRefs(useLanguageStore());
	const { tc } = useI18n();
	const { setProfileJsonLD } = useMetaStore();

	const queryVariables = computed<GetProfileByUrlQueryVariables>(() => ({
		fullPath: route.path,
		country: country.value,
		language: language.value,
		firstArticles: 20,
		firstWtw: 4,
	}));

	const enabled = computed(
		() => !!route.name && ['app.user.author', 'app.user.profile', 'app.user.person'].includes(route.name)
	);
	const profileDataQuery = useQuery<GetProfileByUrlQuery, GetProfileByUrlQueryVariables>(
		GetProfileByUrlDocument,
		queryVariables,
		{ errorPolicy: 'all', enabled }
	);

	profileDataQuery.onError(error => {
		captureMessageForSentry(
			'[GraphQL Get User Profile Page Data]:',
			{ error, where: 'Composable: useUserProfile' },
			'error'
		);

		if (process.client) {
			toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
		}
	});

	profileDataQuery.onResult(({ data }) => {
		if (data) setProfileJsonLD(data.profile!);
	});

	const profile = computed(() => profileDataQuery.result.value?.profile);
	const isLoading = computed(() => profileDataQuery.loading.value);

	/* ARTICLES */
	const articles = computed(() =>
		(profile.value?.articles.edges ?? [])
			.map(({ node }) => ({ article: node, ...getMainObject(node) }))
			.filter(({ mainObject }) => mainObject != null)
	);

	const isAtEndOfArticles = computed(() => !profile.value?.articles.pageInfo.hasNextPage);

	async function loadNextArticles({ event }: { event: { target?: { complete: (len: number) => void } } }) {
		if (event == null) return;

		await profileDataQuery.fetchMore({
			variables: {
				articlesAfter: profile.value?.articles.pageInfo.endCursor,
			},
			updateQuery(previousQueryResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousQueryResult;

				const fetchedArticles = fetchMoreResult?.profile?.articles;
				const previousArticles = previousQueryResult?.profile?.articles;

				if (previousArticles?.edges == null || fetchedArticles?.edges == null) return previousQueryResult;

				previousArticles.pageInfo = fetchedArticles?.pageInfo;
				previousArticles?.edges.push(...(fetchedArticles?.edges ?? []));

				return previousQueryResult;
			},
		});

		event.target?.complete(articles.value.length);
	}

	/* TEXT RECOMMENDATIONS */
	const textRecommendations = computed(() =>
		(profile.value?.textRecommendations.edges ?? []).map(({ node }) => node)
	);
	const textRecommendationCounts = computed(() => ({
		total: profile.value?.textRecommendations.totalCount ?? 0,
	}));

	const isAtEndOfRecommendations = computed(() => !profile.value?.textRecommendations.pageInfo.hasNextPage);

	async function loadNextRecommendations({ event }: { event: { target?: { complete: (len: number) => void } } }) {
		if (event == null) return;

		await profileDataQuery.fetchMore({
			variables: {
				wtwAfter: profile.value?.textRecommendations.pageInfo.endCursor,
				firstWtw: 10,
			},
			updateQuery(previousQueryResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousQueryResult;

				const fetchedRecommendations = fetchMoreResult?.profile?.textRecommendations;
				const previousRecommendations = previousQueryResult?.profile?.textRecommendations;

				if (previousRecommendations?.edges == null || fetchedRecommendations?.edges == null)
					return previousQueryResult;

				previousRecommendations.pageInfo = fetchedRecommendations.pageInfo;
				previousRecommendations.edges.push(...(fetchedRecommendations.edges ?? []));

				return previousQueryResult;
			},
		});
		event.target?.complete(textRecommendations.value.length);
	}

	/* PROFILE INFO */
	const profileInfo = computed(() => {
		return {
			uuid: profile.value?.id,
			firstName: profile.value?.firstName,
			lastName: profile.value?.lastName,
			city: profile.value?.location,
			country: profile.value?.country,
			bio: profile.value?.bio,
			avatarUrl: profile.value?.avatarUrl,
			website: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Website),
			altWebsite: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Other),
			instagram: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Instagram),
			imdb: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Imdb),
			x: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Twitter),
			tiktok: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Tiktok),
			linkedin: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Linkedin),
			facebook: profile.value?.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Facebook),
		};
	});

	const personContentId = computed(() => {
		return profile.value?.contentPersonId;
	});

	/* PROFILE VIEW ROUTING */
	const router = useRouter();
	const isMounted = useMounted();

	async function setView(view: UserProfileView) {
		await router.push({ hash: view });
		nextTick(() => window.scrollTo(0, 0));
	}

	const currentView = computed<UserProfileView>(() => {
		if (!isMounted.value) return 'profile';

		return (route.hash?.substring(1) as UserProfileView) || 'profile';
	});

	return {
		isLoading,

		profile,
		profileInfo,
		personContentId,

		articles,
		isAtEndOfArticles,
		loadNextArticles,

		textRecommendations,
		textRecommendationCounts,
		isAtEndOfRecommendations,
		loadNextRecommendations,

		setView,
		currentView,
	};
}

function getMainObject(article: ArticleNode) {
	const mainObject = article.mainObjects[0];
	if (mainObject == null) return null;

	type Node = typeof mainObject.node;
	const mainObjectNode = mainObject.node as AssertByTypename<Node, 'GenericTitleList'>;

	return {
		totalCount: mainObjectNode.titles.totalCount,
		mainObject: mainObjectNode.titles.edges?.[0].nodeV2,
	};
}
